// AIConsentModal — Apple App Review 5.1.1 / 5.1.2 compliance.
//
// Just-in-time consent sheet shown the FIRST time a user triggers any AI
// feature (AI Coach chat, Voice transcription, Describe meal, Photo meal
// analysis, Nutrition-label OCR). Explains WHAT is sent, TO WHOM, and asks
// for explicit permission with two clear buttons.
//
// Wired to `ensureAIConsent()` in /lib/aiConsent.js via window CustomEvents:
//   - "ai-consent-request"  → opens the modal
//   - "ai-consent-resolved" → dispatched with {accepted: bool} on close
//
// To remove after Apple approves: flip AI_CONSENT_REQUIRED in aiConsent.js
// to `false`. This component stays mounted but never opens.

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, X, Check, Info } from "lucide-react";
import { setAIConsent } from "@/lib/aiConsent";
import { Link } from "react-router-dom";

const COPY = {
  ar: {
    title: "مشاركة بياناتك مع الذكاء الاصطناعي",
    intro:
      "لتحليل وجباتك واستخدام مدرّب الذكاء الاصطناعي، نحتاج إذنك لإرسال المحتوى الذي تكتبه أو تصوّره إلى مزوّدي الذكاء الاصطناعي.",
    whatTitle: "ما الذي يتم إرساله؟",
    what: [
      "نص المحادثة مع المدرّب",
      "صور الأطعمة وملصقات التغذية التي تلتقطها",
      "التسجيلات الصوتية عند استخدام الميكروفون",
    ],
    whoTitle: "إلى من يتم إرساله؟",
    who: [
      "OpenAI (لتحليل الصور والمحادثة)",
      "Google Gemini (احتياطي لتحليل بعض الطلبات)",
    ],
    purposeTitle: "لماذا؟",
    purpose:
      "لحساب السعرات والماكروز، قراءة ملصقات التغذية، وإعطائك ردود المدرّب.",
    privacyLine: "لا نبيع بياناتك، ولا نستخدمها للإعلانات.",
    privacyLink: "اطّلع على سياسة الخصوصية",
    accept: "أوافق ومتابعة",
    decline: "لا أوافق",
    footnote:
      "يمكنك سحب موافقتك في أي وقت من ملفّك الشخصي.",
  },
  en: {
    title: "Share data with AI",
    intro:
      "To analyze your meals and use the AI coach, we need your permission to send the content you type or capture to AI providers.",
    whatTitle: "What is sent?",
    what: [
      "Your chat messages with the coach",
      "Food photos and nutrition-label photos you capture",
      "Voice recordings when you use the microphone",
    ],
    whoTitle: "Who receives it?",
    who: [
      "OpenAI (image analysis and chat)",
      "Google Gemini (fallback for some requests)",
    ],
    purposeTitle: "Why?",
    purpose:
      "To calculate calories and macros, read nutrition labels, and generate coach replies.",
    privacyLine: "We don't sell your data or use it for advertising.",
    privacyLink: "Read the Privacy Policy",
    accept: "Accept & continue",
    decline: "Don't allow",
    footnote: "You can revoke your consent anytime from your Profile.",
  },
};

export default function AIConsentModal() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("ar");

  useEffect(() => {
    const onRequest = (e) => {
      setLang(e.detail?.lang === "en" ? "en" : "ar");
      setOpen(true);
    };
    window.addEventListener("ai-consent-request", onRequest);
    return () => window.removeEventListener("ai-consent-request", onRequest);
  }, []);

  const resolve = (accepted) => {
    setAIConsent(accepted);
    window.dispatchEvent(
      new CustomEvent("ai-consent-resolved", { detail: { accepted } }),
    );
    setOpen(false);
  };

  const t = COPY[lang];
  const isRTL = lang === "ar";

  return (
    <AnimatePresence>
      {open && (
        <div
          dir={isRTL ? "rtl" : "ltr"}
          className="always-light fixed inset-0 z-[110] flex items-end sm:items-center justify-center px-3 sm:px-4"
          data-testid="ai-consent-modal"
        >
          {/* Backdrop — tap to decline (safer default per Apple review) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => resolve(false)}
            className="absolute inset-0 bg-black/55 backdrop-blur-sm"
          />

          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 240 }}
            className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
            style={{ marginBottom: "max(env(safe-area-inset-bottom), 12px)" }}
          >
            {/* Header */}
            <div
              className="relative px-6 pt-6 pb-4"
              style={{
                background:
                  "linear-gradient(180deg, #FFF7EE 0%, rgba(255,247,238,0) 100%)",
              }}
            >
              <button
                type="button"
                onClick={() => resolve(false)}
                className="absolute top-3 end-3 w-9 h-9 rounded-full bg-white/70 hover:bg-white flex items-center justify-center text-neutral-500 active:scale-95 transition"
                aria-label="Close"
                data-testid="ai-consent-close"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl orange-gradient text-white flex items-center justify-center shadow-brand flex-shrink-0">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-black text-neutral-900 leading-tight">
                    {t.title}
                  </h2>
                </div>
              </div>
              <p className="text-[13.5px] leading-relaxed text-neutral-700 mt-3">
                {t.intro}
              </p>
            </div>

            {/* Body */}
            <div className="px-6 pb-4 max-h-[52vh] overflow-y-auto space-y-4">
              <Section title={t.whatTitle} items={t.what} />
              <Section title={t.whoTitle} items={t.who} />
              <div>
                <div className="text-xs font-black text-neutral-500 uppercase tracking-wider mb-1.5">
                  {t.purposeTitle}
                </div>
                <p className="text-[13.5px] leading-relaxed text-neutral-700">
                  {t.purpose}
                </p>
              </div>
              <div className="flex items-start gap-2 rounded-2xl bg-emerald-50 border border-emerald-100 p-3">
                <Info className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-[12.5px] leading-relaxed text-emerald-900">
                  {t.privacyLine}{" "}
                  <Link
                    to="/legal"
                    onClick={() => resolve(false)}
                    className="underline font-semibold"
                    data-testid="ai-consent-privacy-link"
                  >
                    {t.privacyLink}
                  </Link>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="px-5 pt-2 pb-5 border-t border-neutral-100 bg-white">
              <button
                type="button"
                onClick={() => resolve(true)}
                className="w-full orange-gradient text-white font-black py-3.5 rounded-full shadow-floating active:scale-[0.98] transition flex items-center justify-center gap-2"
                data-testid="ai-consent-accept"
              >
                <Check className="w-4 h-4" strokeWidth={3} />
                {t.accept}
              </button>
              <button
                type="button"
                onClick={() => resolve(false)}
                className="w-full mt-2 bg-neutral-100 text-neutral-700 font-semibold py-3 rounded-full active:scale-[0.98]"
                data-testid="ai-consent-decline"
              >
                {t.decline}
              </button>
              <p className="text-[11px] text-neutral-500 text-center mt-2.5 leading-snug">
                {t.footnote}
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function Section({ title, items }) {
  return (
    <div>
      <div className="text-xs font-black text-neutral-500 uppercase tracking-wider mb-1.5">
        {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li
            key={i}
            className="flex items-start gap-2 text-[13.5px] text-neutral-800"
          >
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-500 flex-shrink-0" />
            <span className="leading-relaxed">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
